import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import { useDidMountEffect } from '../../../hooks/useDidMountEffect';
import { DatePicker } from '../../form';
import { DropdownMenu } from '../../form';
import { Icon } from '../../UI';
import Calendar from './Calendar';
import {
  extractCalendarData,
  getStartEndOf,
  useEconomicCalendar,
} from './helpers';
import { TodaySelector } from './Utils';
import { GET_NEWS_CALENDAR } from '../../../http/apollo-queries';
import { Loading } from '../../UI';
import {
  CALENDAR_VIEW,
  EconomicCalendarProps,
  EventsApiResponse,
  VIEWS,
} from './types';
import EventsFilter from './EventsFilter';
import { useErrorHandler } from 'react-error-boundary';
import { logger } from '../../../helpers/logger';
import SearchAsset from '../SearchAsset';

const EconomicCalendar: React.FC<EconomicCalendarProps> = ({
  width,
  setLastWidgetUpdateAt,
}) => {
  const { t } = useTranslation();
  const onError = useErrorHandler();

  // Calendar Data
  const [
    getCalendarEvents,
    {
      data,
      loading: loadingCalendarEvents,
      // refetch: getCalendarEvents_,
    },
  ] = useLazyQuery<EventsApiResponse>(GET_NEWS_CALENDAR, {
    notifyOnNetworkStatusChange: true,
    onError,
    onCompleted: () => setLastWidgetUpdateAt?.(new Date()),
  });

  /* this will be selected filters */
  const [filters, setFilters] = useState<string[]>([]);

  const [tickers, setTickers] = useState<string[]>([]);

  // Calendar UI
  const { calendarView, calendarRef, getCalendarInstance, setCalendarView } =
    useEconomicCalendar();

  // Date picker
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    getCalendarInstance()?.gotoDate(selectedDate);
  }, [getCalendarInstance, selectedDate]);

  useDidMountEffect(() => {
    getCalendarEvents({
      variables: {
        ...getStartEndOf(
          selectedDate,
          calendarView === 'dayGridMonth' ? 'month' : 'isoWeek'
        ),
        eventClass: filters,
        tickers,
        onlyKeyEvents: !filters?.length && !tickers?.length,
        limit: 1000,
      },
    }).then();
  }, [selectedDate, calendarView, filters, tickers]);

  const handleChoose = () => {};
  const events = data?.getNewsCalendar?.items || [];

  return (
    <div className="overflow-hidden flex flex-col h-full">
      {/* HEADER */}
      <div className="border-b border-grey-10 flex dark:border-grey-80">
        {/* SEARCH, DATE PICKER */}
        <div
          className={`border-r border-grey-10 dark:border-grey-80 text h-full px-4 py-2 flex items-center justify-between ${
            width === 1 ? '' : 'w-1/2'
          }`}
        >
          <SearchAsset
            types={['STOCK']}
            onChoose={(asset) => {
              setTickers([asset.symbol]);
            }}
            isDeletable={true}
            onDelete={() => {
              setTickers([]);
            }}
          />

          <DatePicker
            size="small"
            onChange={(date) => setSelectedDate(date as Date)}
            date={selectedDate}
            hasBorderBottom={false}
            id={'economicCalendarDate'}
            inputClassName="pl-5"
            inputDateFormatter={(selectedDate) => {
              const date = moment(selectedDate);
              let start, end;
              if (calendarView === VIEWS.week) {
                start = date.startOf('isoWeek').toDate();
                end = date.endOf('isoWeek').toDate();
              } else {
                start = date.startOf('month').toDate();
                end = date.endOf('month').toDate();
              }
              return `${start.getDate()} - ${end.getDate()} ${date.format(
                'MMM'
              )} ${date.format('yyyy')}`;
            }}
            icon={<Icon svgName="arrowDown" />}
          />
        </div>
        {/* CALENDAR VIEW , FILTERS */}
        <div className="flex text-sm text-grey-50 w-1/2">
          {width !== 1 && (
            <DropdownMenu
              dropdownClassName={`flex items-center border-r border-grey-10 font-medium py-5 pr-8 pl-4 cursor-pointer w-24 dark:border-grey-80`}
              dropdownTooltipClassName={`bg-white border-solid border border-grey-10 -mt-3`}
              dropdownArrowClassName="-right-7 text-black -mt-px"
              dropdownItems={[
                <span className="text-sm">Week</span>,
                <span className="text-sm">Month</span>,
              ]}
              dropdownItemsData={[VIEWS.week, VIEWS.month]}
              handleClick={(_, view: CALENDAR_VIEW) => setCalendarView(view)}
              showArrow
              hideOnItemClick
            >
              {t('widget.economicCalendar.' + calendarView)}
            </DropdownMenu>
          )}
          {/* TODAY SELECTOR */}
          <TodaySelector
            setToday={() => setSelectedDate(new Date())}
            width={width}
            setCalendarView={setCalendarView}
          />
          <div className="mr-4 w-full">
            <EventsFilter width={width} onFiltersChange={setFilters} />
          </div>
        </div>
      </div>

      {logger.log('EconomicCalendar', extractCalendarData(events))}

      {loadingCalendarEvents ? (
        <div className={'flex w-full h-full items-center justify-center'}>
          <Loading size={'large'} status={loadingCalendarEvents} />
        </div>
      ) : (
        <Calendar
          view={calendarView}
          ref={calendarRef}
          width={width}
          events={extractCalendarData(events)}
        />
      )}
    </div>
  );
};

export default EconomicCalendar;
